<template>
  <modal
    class="wtHolidays"
    :header-text="$t('modal.editCalendar')"
    hide-footer="true"
    :width="50"
    @close="close()"
  >
    <div :class="$style.header">
      <div :class="$style.addDate">
        <date-picker
          v-model="valueDate"
          type="date"
          value-type="format"
          :clearable="false"
          :format="'DD.MM.YYYY'"
          :default-value="dayValue"
        />
        <div
          :class="$style.customCheckBoxContainer"
          @click="checkDay()"
        >
          <input
            v-model="checkWorkday"
            type="checkbox"
            :class="$style.customCheckbox"
          >
          <label>
            <check-mark v-if="checkWorkday" />
            <div :class="$style.textContainer">{{ $t('global.shortenedDay') }}</div>
          </label>
        </div>
        <button
          :class="$style.button"
          @click="addDate()"
        >
          {{ $t('global.add') }}
        </button>
      </div>
      <date-picker
        v-model="currentYear"
        type="year"
        value-type="format"
        :clearable="false"
        :format="'YYYY'"
        :default-value="yearValue"
      />
    </div>

    <div :class="$style.container">
      <div>
        <div :class="$style.wrapper">
          <div
            :class="$style.expandArea"
            @click="isHoliday = !isHoliday"
          >
            <div :class="[$style.expandButton, { [$style.expanded]: isHoliday }]" />
          </div>
          <div>{{ $t('global.weekendDays') }}</div>
        </div>
        <div
          v-if="isHoliday"
          :class="$style.grid"
        >
          <div
            v-for="day in holidaysData.holidays"
            :key="day"
            :class="$style.gridItem"
            @mouseover="showDeleteButton(day)"
            @mouseleave="hideDeleteButton(day)"
          >
            <div>{{ day }}</div>
            <img
              v-show="hoveredIndex === day"
              :title="$t('global.delete')"
              src="@/assets/images/icons/navigation/trash.svg"
              :class="$style.deleteIcon"
              @click="openDeleteModal(day, 'holidays')"
            >
          </div>
        </div>
      </div>
      <div>
        <div :class="$style.wrapper">
          <div
            :class="$style.expandArea"
            @click="isWorkday = !isWorkday"
          >
            <div :class="[$style.expandButton, { [$style.expanded]: isWorkday }]" />
          </div>
          <div>{{ $t('global.shortenedDays') }}</div>
        </div>
        <div
          v-if="isWorkday"
          :class="$style.grid"
        >
          <div
            v-for="day in holidaysData.workdays"
            :key="day"
            :class="$style.gridItem"
            @mouseover="showDeleteButton(day)"
            @mouseleave="hideDeleteButton(day)"
          >
            <div>{{ day }}</div>
            <img
              v-show="hoveredIndex === day"
              :title="$t('global.delete')"
              src="@/assets/images/icons/navigation/trash.svg"
              :class="$style.deleteIcon"
              @click="openDeleteModal(day, 'workdays')"
            >
          </div>
        </div>
      </div>
    </div>
    <delete-user-modal
      id="deleteDate"
      :class="$style.addModalDelete"
      :selected="selectedDate"
      type="date"
      @deleteDate="deleteDate()"
      @close="closeDeleteModal()"
    />
  </modal>
</template>

<script>
  import Vue from 'vue';
  import Modal from '@/components/common/modal/settings-modal.vue';
  import CheckMark from '@/components/common/filter/check-mark.vue';
  import DeleteUserModal from '@/views/settings/agents/modals/deleteModal.vue';
  import { getHolidays, deleteHoliday, addHoliday } from '@/api/methods/holidays';
  import DatePicker from 'vue2-datepicker';
  import dayjs from 'dayjs';
  import VueToast from 'vue-toast-notification';

  Vue.use(VueToast);

  export default {
    components: { Modal, DatePicker, DeleteUserModal, CheckMark },

    data: () => ({
      holidaysData: {},
      currentYear: dayjs().format('YYYY'),
      valueDate: dayjs().format('DD.MM.YYYY'),
      hoveredIndex: null,
      selectedType: null,
      selectedDate: null,
      checkWorkday: false,
      isHoliday: false,
      isWorkday: false,
    }),
    computed: {
      dayValue() {
        return `'${dayjs().format('DD-MM-YYYY')}''`;
      },
      yearValue() {
        return `'${new Date().getFullYear}'`;
      },
    },
    watch: {
      async currentYear(newValue, oldValue) {
        if (newValue !== oldValue) {
          await this.getData(newValue);
        }
      },
    },
    async mounted() {
      await this.getData(this.currentYear);
    },
    methods: {
      async getData(year) {
        try {
          const { data } = await getHolidays(year);
          this.holidaysData = data;
        } catch (error) {
          console.error(this.$t('toast.loadingHolidays'), error);
          Vue.$toast.open({
            message: this.$t('toast.loadingHolidays'),
            type: 'error',
          });
        }
      },
      async deleteDate() {
        if (this.selectedDate !== null && this.selectedType !== null) {
          const indexToRemove = this.holidaysData[this.selectedType].indexOf(this.selectedDate);

          if (indexToRemove !== -1) {
            try {
              const data = await deleteHoliday(this.selectedDate);

              if (data.status === 200) {
                this.holidaysData[this.selectedType].splice(indexToRemove, 1);
                Vue.$toast.open({
                  message: `${this.$t('toast.date')} ${this.selectedDate} ${this.$t('toast.dateRemovedFromCalendar')}`,
                  type: 'success',
                });
              } else {
                Vue.$toast.open({
                  message: this.$t('error.somethingWentWrong'),
                  type: 'error',
                });
              }
            } catch (error) {
              console.error(this.$t('error.somethingWentWrong'), error);
              Vue.$toast.open({
                message: this.$t('error.somethingWentWrong'),
                type: 'error',
              });
            } finally {
              this.selectedDate = null;
              this.selectedType = null;
            }
          } else {
            Vue.$toast.open({
              message: `${this.$t('toast.date')} ${this.selectedDate} ${this.$t('toast.dateNotFoundInCalendar')}`,
              type: 'error',
            });
          }
        }
      },

      async addDate() {
        const year = this.valueDate.split('.')[2];
        if (this.currentYear !== year) {
          Vue.$toast.open({
            message: `${this.$t('toast.selectYear')} ${this.currentYear}`,
            type: 'error',
          });
          return;
        }
        const dateArray = this.checkWorkday ? this.holidaysData.workdays : this.holidaysData.holidays;

        const dateExistsInWorkdays = this.holidaysData.workdays.includes(this.valueDate);
        const dateExistsInHolidays = this.holidaysData.holidays.includes(this.valueDate);

        if (!dateExistsInWorkdays && !dateExistsInHolidays) {
          const data = await addHoliday(this.valueDate, this.checkWorkday ? 2 : 1);
          if (data.status === 200) {
            dateArray.push(this.valueDate);

            dateArray.sort((a, b) => {
              const dateA = a.split('.').reverse().join('-');
              const dateB = b.split('.').reverse().join('-');
              return new Date(dateA) - new Date(dateB);
            });

            this.holidaysData[this.checkWorkday ? 'workdays' : 'holidays'] = dateArray;
            Vue.$toast.open({
              message: `${this.$t('toast.date')} ${this.valueDate} ${this.$t('toast.addedToCalendar')}`,
              type: 'success',
            });
          }
        } else {
          Vue.$toast.open({
            message: `${this.$t('toast.date')} ${this.valueDate} ${this.$t('toast.alreadyInCalendar')}`,
            type: 'error',
          });
        }
      },

      showDeleteButton(day) {
        this.hoveredIndex = day;
      },
      hideDeleteButton() {
        this.hoveredIndex = null;
      },
      close() {
        this.isHoliday = false;
        this.isWorkday = false;
        this.$emit('close');
      },
      openDeleteModal(date, type) {
        this.selectedDate = date;
        this.selectedType = type;
        document.getElementById('deleteDate').style.display = 'flex';
      },
      closeDeleteModal() {
        document.getElementById('deleteDate').style.display = 'none';
      },
      checkDay() {
        this.checkWorkday = !this.checkWorkday;
      },
    },
  };
</script>

<style lang="scss" module>
  .container {
    display: grid;
    grid-template-columns: 200px 200px;
    gap: 50px;
    height: 450px;
    margin: 0 50px;
  }
  .wrapper {
    display: flex;
    align-items: center;
    width: 100%;
    min-height: 50px;
  }

  .grid {
    margin: 0 15px;
    max-height: 400px;
    overflow-y: auto;
  }

  .grid::-webkit-scrollbar {
    display: none;
  }

  .gridItem {
    margin: 10px;
    font-weight: 300;
    font-size: 14px;
    cursor: pointer;
    gap: 7px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    justify-content: space-around;
    align-items: center;
    height: 18px;
  }

  .expandArea {
    cursor: pointer;
    display: flex;
    height: 30px;
    align-items: center;
  }

  .expandButton {
    flex-shrink: 0;
    margin-right: 13px;
    width: 14px;
    height: 14px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    border: 1px solid $branchColor;
    background: $white;

    &::before,
    &::after {
      content: '';
      background-color: $blue;
      width: 8px;
      height: 2px;
      position: absolute;
      transition: 0.25s ease-out;
    }

    &::before {
      transform: rotate(90deg);
    }

    &::after {
      transform: rotate(180deg);
    }

    &.expanded {
      &::before {
        transform: rotate(0);
      }

      &::after {
        transform: rotate(0);
      }
    }
  }

  .addModalDelete {
    display: none;
    z-index: 5;
  }

  .datepicker {
    cursor: pointer;
  }

  .datepickerPopup td {
    vertical-align: middle;
  }

  .filter {
    border: 1px solid #dfdfdf;
    display: flex;
    height: 50px;
  }

  .dateWrapper {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    padding: 6px 15px;
    min-width: 107px;

    &:first-child {
      border-right: 1px solid #dfdfdf;
    }
  }

  .dateText {
    color: #7e7e7e;
    font-size: 12px;
    margin-bottom: 8px;
  }

  .date {
    font-size: 14px;
  }

  .header {
    display: flex;
    gap: 30px;
    margin: 0 20px;
  }
  .addDate {
    display: flex;
    gap: 10px;
  }
  .button {
    cursor: pointer;
    width: 90px;
    background: #2985bf;
    border: none;
    border-radius: 2px;
    font: inherit;
    font-size: 14px;
    text-align: center;
    color: #ffffff;
    opacity: 1;
    transition: all 0.3 ease-in-out;
  }
  .button:hover {
    opacity: 0.8;
  }
  .button:active {
    background: rgb(73, 173, 106);
  }
  .customCheckBoxContainer {
    .customCheckbox {
      border: 0;
      clip: rect(0 0 0 0);
      height: 1px;
      margin: -1px;
      overflow: hidden;
      padding: 0;
      position: absolute;
      width: 1px;
    }

    input:checked + label > svg {
      animation: draw-checkbox ease-in-out 0.2s forwards;
    }

    label:active::after {
      background-color: #fbfbfb;
    }

    label {
      color: black;
      font-style: normal;
      font-weight: 400;
      font-size: 14px;
      line-height: 30px;
      cursor: pointer;
      position: relative;
      display: block;

      &:after {
        content: '';
        height: 14px;
        width: 14px;
        float: left;
        border: 1px solid #9f9f9f;
        border-radius: 4px;
        transition: 0.15s all ease-out;
        background-color: white;
        margin: 9px 0;
      }
    }

    input:checked + label:after {
      background-color: $blue;
      border: 1px solid $blue;
    }

    svg {
      position: absolute;
      left: 4px;
      top: 13px;
      stroke-dasharray: 33;
    }

    @keyframes draw-checkbox {
      0% {
        stroke-dashoffset: 33;
      }

      100% {
        stroke-dashoffset: 0;
      }
    }
  }

  .textContainer {
    white-space: nowrap;
    text-overflow: ellipsis;
    display: inline-block;
    line-height: 21px;
    padding-left: 10px;
    padding-top: 6px;
  }
</style>
